import type { ActivityType, RecurrenceStats } from "@lifelog/shared";
import { ActivityIcon } from "./icons.js";

export function ActivityButtonGrid({
  types,
  statsByTypeId,
  loggedToday,
  pendingTypeId,
  onTap,
}: {
  types: ActivityType[];
  statsByTypeId: Record<string, RecurrenceStats>;
  loggedToday: Set<string>;
  pendingTypeId?: string | null;
  onTap: (type: ActivityType) => void;
}) {
  if (types.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        No activities yet. Use Chat to describe something, or pin types once they exist.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
      {types.map((type) => {
        const stats = statsByTypeId[type.activityTypeId];
        const overdue = stats?.dueStatus === "overdue";
        const dueSoon = stats?.dueStatus === "due_soon";
        const today = loggedToday.has(type.activityTypeId);
        const pending = pendingTypeId === type.activityTypeId;
        const overdueLabel =
          overdue && stats?.overdueByDays != null ? `${Math.round(stats.overdueByDays)}d` : null;

        return (
          <button
            key={type.activityTypeId}
            type="button"
            onClick={() => onTap(type)}
            disabled={pending}
            aria-label={type.name}
            className={
              "relative flex min-h-16 flex-col items-center justify-center gap-1 rounded-xl border bg-white px-1 py-2 text-center " +
              "hover:bg-gray-50 disabled:opacity-60 dark:bg-gray-900 dark:hover:bg-gray-800 " +
              (today ? "border-(--accent)" : "border-gray-200 dark:border-gray-800")
            }
            style={{ color: type.colorToken }}
          >
            {overdueLabel && (
              <span className="absolute right-1 top-1 rounded-full bg-red-600 px-1.5 py-px text-[10px] font-semibold text-white">
                {overdueLabel}
              </span>
            )}
            {dueSoon && !overdue && (
              <span
                className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-amber-400"
                aria-label="Due soon"
              />
            )}
            <ActivityIcon name={type.iconName} size={28} />
            <span className="max-w-full truncate text-[11px] font-medium leading-tight text-gray-800 dark:text-gray-200">
              {type.shortName}
            </span>
          </button>
        );
      })}
    </div>
  );
}
